import { useCallback, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiFetch,
  apiJson,
  ApiError,
  SESSION_PATH,
  SESSION_QUERY_KEY,
  setCsrfToken,
  setCurrentOrgSlug,
} from "../api/client";
import { postLogout } from "../api/authChannel";
import type { AuthUser, OrgMembership, OrgRole, Role, SessionResponse } from "./types";

/** Idle timeout — triggers a session recheck before the backend's 30min expiry. */
const IDLE_TIMEOUT_MS = 25 * 60 * 1000;
/** Check for idle every 60 seconds. */
const IDLE_CHECK_INTERVAL_MS = 60 * 1000;

export function useAuth() {
  const queryClient = useQueryClient();
  const lastActivityRef = useRef(Date.now());

  const {
    data: session,
    isLoading,
    error,
  } = useQuery<SessionResponse>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async () => {
      try {
        const data = await apiJson<SessionResponse>(SESSION_PATH);
        if (data.csrfToken) {
          setCsrfToken(data.csrfToken);
        }
        return data;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          return { authenticated: false };
        }
        throw e;
      }
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: 4 * 60_000,
    // Boot UX: a 401 already short-circuits to authenticated:false above, so
    // the only failures left are network/5xx. Fail fast (single retry, 500ms)
    // so the disconnected screen appears within ~1s instead of ~3s.
    retry: 1,
    retryDelay: 500,
  });

  // Track user activity for idle detection
  useEffect(() => {
    const onActivity = () => {
      lastActivityRef.current = Date.now();
    };

    window.addEventListener("keydown", onActivity, { passive: true });
    window.addEventListener("click", onActivity, { passive: true });

    const interval = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle > IDLE_TIMEOUT_MS && session?.authenticated) {
        queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      }
    }, IDLE_CHECK_INTERVAL_MS);

    return () => {
      window.removeEventListener("keydown", onActivity);
      window.removeEventListener("click", onActivity);
      clearInterval(interval);
    };
  }, [session?.authenticated, queryClient]);

  const user: AuthUser | null = session?.user ?? null;
  const isAuthenticated = session?.authenticated === true && user !== null;
  const roles: Role[] = user?.roles ?? [];
  const webauthnEnabled = session?.webauthnEnabled ?? false;
  const stepUpPolicy = session?.stepUpPolicy ?? {};

  const orgs: OrgMembership[] = user?.orgs ?? [];
  const currentOrgSlug = session?.currentOrgSlug ?? null;
  const currentOrg: OrgMembership | null = (() => {
    if (!user?.current_org_id) return null;
    return orgs.find((o) => o.organization_id === user.current_org_id) ?? null;
  })();
  const isPlatformAdmin = roles.includes("platform_admin");

  // Mirror the active org slug into the API client so org-scoped fetches
  // (everything under `/api/orgs/{slug}/...`) can prefix without prop-drilling.
  // Backend resolves this for both members and platform admins, so it works
  // even when the user is not a member of the current org.
  useEffect(() => {
    setCurrentOrgSlug(currentOrgSlug);
  }, [currentOrgSlug]);

  const login = useCallback(() => {
    window.location.href = "/auth/login";
  }, []);

  const logout = useCallback(async () => {
    // `suppressUnauthorized` keeps the global handler from racing the
    // `window.location.href` below if the backend returns 401 (session already
    // gone). The hard reload is the authoritative redirect for logout.
    await apiFetch("/auth/logout", {
      method: "POST",
      suppressUnauthorized: true,
    });
    queryClient.setQueryData(SESSION_QUERY_KEY, { authenticated: false });
    postLogout();
    window.location.href = "/login";
  }, [queryClient]);

  const switchOrg = useCallback(
    async (slug: string) => {
      await apiFetch("/auth/switch-org", {
        method: "POST",
        body: JSON.stringify({ slug }),
      });
      // Drop org-scoped caches before the session refetch so the UI can't
      // flash the prior org's data. Session + platform-admin keys are global.
      queryClient.removeQueries({
        predicate: (q) => {
          const first = q.queryKey[0];
          if (first === "auth") return false;
          if (first === "admin") return false;
          return true;
        },
      });
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
    [queryClient],
  );

  const hasRole = useCallback(
    (role: Role): boolean => roles.includes(role),
    [roles],
  );

  const hasAnyRole = useCallback(
    (checkRoles: Role[]): boolean => checkRoles.some((r) => roles.includes(r)),
    [roles],
  );

  /**
   * Check whether the user holds at least the given role within the active
   * org. Platform admins always satisfy any per-org check.
   */
  const hasOrgRole = useCallback(
    (required: OrgRole): boolean => {
      if (isPlatformAdmin) return true;
      if (!currentOrg) return false;
      return orgRank(currentOrg.role) <= orgRank(required);
    },
    [currentOrg, isPlatformAdmin],
  );

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    roles,
    webauthnEnabled,
    stepUpPolicy,
    orgs,
    currentOrg,
    currentOrgSlug,
    isPlatformAdmin,
    login,
    logout,
    switchOrg,
    hasRole,
    hasAnyRole,
    hasOrgRole,
  };
}

function orgRank(role: OrgRole): number {
  switch (role) {
    case "admin":
      return 1;
    case "reviewer":
      return 2;
    case "publisher":
      return 3;
    case "viewer":
      return 4;
  }
}

export interface AuthContext {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  roles: Role[];
  webauthnEnabled: boolean;
  stepUpPolicy: Record<string, boolean>;
  orgs: OrgMembership[];
  /**
   * The user's membership in the active org, if any. `null` for platform
   * admins who picked a non-member org - check `currentOrgSlug` to know
   * whether they have an active org at all.
   */
  currentOrg: OrgMembership | null;
  /**
   * Slug of the active org. Set whenever the user has an active org context,
   * regardless of membership. Use this (not `currentOrg`) to gate "is the
   * user in an org" decisions.
   */
  currentOrgSlug: string | null;
  isPlatformAdmin: boolean;
  login: () => void;
  logout: () => Promise<void>;
  switchOrg: (slug: string) => Promise<void>;
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: Role[]) => boolean;
  hasOrgRole: (required: OrgRole) => boolean;
}
